import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ArrowForward,
  DeleteOutline,
  GavelOutlined,
  LockOutlined,
  MailOutline,
  SecurityOutlined,
} from "@material-ui/icons";

import fluyraLogo from "../../assets/fluyra-logo-dark.png";

export const LEGAL_CONTACT_EMAIL = "privacidade@fluyra.com.br";
export const LEGAL_ENTITY_NAME =
  "F T DE OLIVEIRA FABIO TRAGUETTA DE OLIVEIRA SERVICOS DE APOIO ADMINISTRATIVO";

const EFFECTIVE_DATE = "14 de setembro de 2026";
const PRODUCTION_ORIGIN = "https://chat.fluyra.com.br";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: theme.palette.type === "dark" ? "#071218" : "#f5f7f9",
    color: theme.palette.text.primary,
  },
  skipLink: {
    position: "fixed",
    top: -80,
    left: theme.spacing(2),
    zIndex: 1500,
    padding: theme.spacing(1, 2),
    borderRadius: 10,
    color: "#ffffff",
    background: theme.palette.primary.main,
    textDecoration: "none",
    transition: "top 150ms ease",
    "&:focus": {
      top: theme.spacing(2),
    },
  },
  header: {
    borderBottom: "1px solid rgba(241, 241, 241, 0.08)",
    background: "#07181f",
  },
  headerInner: {
    minHeight: 74,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      minHeight: 66,
      gap: theme.spacing(1.5),
    },
  },
  logoLink: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    width: 184,
    height: 46,
    overflow: "hidden",
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: 148,
      height: 40,
    },
  },
  logo: {
    position: "absolute",
    top: "50%",
    left: "50%",
    display: "block",
    width: 316,
    height: "auto",
    maxWidth: "none",
    transform: "translate(-50%, -50%)",
    [theme.breakpoints.down("xs")]: {
      width: 260,
    },
  },
  headerAction: {
    color: "#f1f1f1",
    borderColor: "rgba(241, 241, 241, 0.18)",
    borderRadius: 999,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      background: "rgba(255, 0, 82, 0.08)",
    },
  },
  hero: {
    position: "relative",
    overflow: "hidden",
    color: "#f1f1f1",
    background: "#0a1b22",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.down("sm")]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5),
    },
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(0.75, 1.5),
    border: "1px solid rgba(255, 0, 82, 0.3)",
    borderRadius: 999,
    color: "#ff7aa5",
    background: "rgba(255, 0, 82, 0.08)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  heroTitle: {
    maxWidth: 820,
    color: "#ffffff",
    fontSize: "clamp(2rem, 5vw, 3.75rem)",
    lineHeight: 1.08,
  },
  heroSubtitle: {
    maxWidth: 760,
    marginTop: theme.spacing(2.5),
    color: "rgba(241, 241, 241, 0.74)",
    fontSize: "clamp(1rem, 2vw, 1.18rem)",
    lineHeight: 1.7,
  },
  heroMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  metaChip: {
    color: "rgba(241, 241, 241, 0.84)",
    borderColor: "rgba(241, 241, 241, 0.16)",
    background: "rgba(241, 241, 241, 0.04)",
  },
  pageBody: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(8),
  },
  legalNav: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  navButton: {
    borderRadius: 999,
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(210px, 260px) minmax(0, 1fr)",
    gap: theme.spacing(4),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(2.5),
    },
  },
  toc: {
    position: "sticky",
    top: theme.spacing(3),
    padding: theme.spacing(0.5, 0, 0.5, 2.25),
    borderLeft: `2px solid ${theme.palette.divider}`,
    borderRadius: 0,
    background: "transparent",
    boxShadow: "none",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  tocTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 700,
  },
  tocLink: {
    display: "block",
    padding: theme.spacing(0.75, 0),
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.4,
    textDecoration: "none",
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
      textUnderlineOffset: 3,
    },
  },
  article: {
    minWidth: 0,
    padding: theme.spacing(0, 4),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    background: theme.palette.background.paper,
    boxShadow: "none",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 2.25),
      borderRadius: 12,
    },
  },
  introCard: {
    padding: theme.spacing(3, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.5, 0),
    },
  },
  introRow: {
    display: "grid",
    gridTemplateColumns: "48px minmax(0, 1fr)",
    gap: theme.spacing(2),
    alignItems: "start",
  },
  introIcon: {
    width: 48,
    height: 48,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 15,
    color: theme.palette.primary.main,
    background: theme.fluyra.tokens.accentSoft,
  },
  section: {
    scrollMarginTop: theme.spacing(3),
    padding: theme.spacing(4, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(3, 0),
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(1.75),
    fontSize: "clamp(1.2rem, 2.5vw, 1.55rem)",
    lineHeight: 1.3,
  },
  paragraph: {
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    lineHeight: 1.78,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  list: {
    margin: theme.spacing(1.5, 0, 0),
    paddingLeft: theme.spacing(2.75),
    color: theme.palette.text.secondary,
    "& li": {
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(0.5),
      lineHeight: 1.68,
    },
    "& li::marker": {
      color: theme.palette.primary.main,
    },
  },
  orderedList: {
    margin: theme.spacing(1.5, 0, 0),
    paddingLeft: theme.spacing(3),
    color: theme.palette.text.secondary,
    "& li": {
      marginBottom: theme.spacing(1.25),
      paddingLeft: theme.spacing(0.75),
      lineHeight: 1.68,
    },
    "& li::marker": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },
  callout: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2.25),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: "0 16px 16px 0",
    color: theme.palette.text.secondary,
    background: theme.fluyra.tokens.accentSoft,
    lineHeight: 1.7,
  },
  inlineLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: "none",
    overflowWrap: "anywhere",
    "&:hover, &:focus": {
      textDecoration: "underline",
      textUnderlineOffset: 3,
    },
  },
  contactCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 0, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  contactLabel: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  contactEmail: {
    display: "inline-block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.primary,
    fontWeight: 700,
    textDecoration: "none",
    overflowWrap: "anywhere",
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },
  contactButton: {
    flexShrink: 0,
    borderRadius: 999,
  },
  footer: {
    color: "rgba(241, 241, 241, 0.68)",
    background: "#0a1b22",
  },
  footerInner: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  footerLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  footerLink: {
    color: "rgba(241, 241, 241, 0.82)",
    textDecoration: "none",
    "&:hover, &:focus": {
      color: "#ffffff",
      textDecoration: "underline",
    },
  },
  legalName: {
    overflowWrap: "anywhere",
  },
}));

const legalPages = {
  privacy: {
    path: "/politica-de-privacidade",
    title: "Política de Privacidade",
    eyebrow: "Privacidade e proteção de dados",
    description:
      "Saiba como o Fluyra coleta, utiliza, compartilha, protege e elimina dados pessoais.",
    icon: SecurityOutlined,
  },
  deletion: {
    path: "/exclusao-de-dados",
    title: "Exclusão de dados",
    eyebrow: "Solicitação do titular",
    description:
      "Veja como solicitar a exclusão de dados do Fluyra e revogar uma integração com a Meta.",
    icon: DeleteOutline,
  },
  terms: {
    path: "/termos-de-uso",
    title: "Termos de Uso",
    eyebrow: "Condições de utilização",
    description:
      "Conheça as condições gerais para acesso e utilização da plataforma Fluyra.",
    icon: GavelOutlined,
  },
};

const Section = ({ id, title, children, classes }) => (
  <section id={id} className={classes.section} aria-labelledby={`${id}-title`}>
    <Typography
      id={`${id}-title`}
      component="h2"
      variant="h5"
      className={classes.sectionTitle}
    >
      {title}
    </Typography>
    {children}
  </section>
);

const Paragraph = ({ children, classes }) => (
  <Typography component="p" className={classes.paragraph}>
    {children}
  </Typography>
);

const BulletList = ({ children, classes }) => (
  <ul className={classes.list}>{children}</ul>
);

const ContactCard = ({ classes, subject = "Privacidade e dados pessoais" }) => (
  <div className={classes.contactCard}>
    <div>
      <div className={classes.contactLabel}>Canal de privacidade</div>
      <a
        className={classes.contactEmail}
        href={`mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`}
      >
        {LEGAL_CONTACT_EMAIL}
      </a>
    </div>
    <Button
      component="a"
      href={`mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`}
      variant="contained"
      color="primary"
      startIcon={<MailOutline />}
      className={classes.contactButton}
    >
      Enviar solicitação
    </Button>
  </div>
);

const PrivacyContent = ({ classes }) => {
  const sections = [
    ["visao-geral", "1. Visão geral"],
    ["quem-somos", "2. Quem somos"],
    ["papeis", "3. Nossos papéis no tratamento"],
    ["dados", "4. Dados que tratamos"],
    ["integracoes-meta", "5. Facebook e Instagram"],
    ["finalidades", "6. Como utilizamos os dados"],
    ["bases-legais", "7. Bases legais"],
    ["compartilhamento", "8. Compartilhamento"],
    ["transferencias", "9. Transferências internacionais"],
    ["retencao", "10. Retenção e eliminação"],
    ["direitos", "11. Direitos dos titulares"],
    ["cookies", "12. Cookies e armazenamento local"],
    ["seguranca", "13. Segurança"],
    ["menores", "14. Crianças e adolescentes"],
    ["alteracoes", "15. Alterações desta política"],
    ["contato", "16. Contato"],
  ];

  return {
    sections,
    intro: (
      <>
        Esta política explica, em linguagem objetiva, como os dados pessoais são
        tratados quando alguém utiliza o Fluyra ou interage com empresas que
        usam a plataforma para atendimento e relacionamento com clientes.
      </>
    ),
    content: (
      <>
        <Section id="visao-geral" title="1. Visão geral" classes={classes}>
          <Paragraph classes={classes}>
            O Fluyra é uma plataforma de CRM conversacional que centraliza
            canais de atendimento, contatos, conversas, automações e atividades
            comerciais. Esta Política de Privacidade se aplica ao site, à
            plataforma e às integrações disponibilizadas pelo Fluyra.
          </Paragraph>
          <Paragraph classes={classes}>
            Tratamos apenas os dados adequados e necessários para oferecer os
            serviços contratados, manter a segurança da operação, prestar
            suporte e cumprir obrigações legais. Não comercializamos dados
            pessoais.
          </Paragraph>
        </Section>

        <Section id="quem-somos" title="2. Quem somos" classes={classes}>
          <Paragraph classes={classes}>
            O Fluyra é operado por{" "}
            <strong className={classes.legalName}>{LEGAL_ENTITY_NAME}</strong>,
            responsável por este aviso e pelo canal de atendimento aos titulares
            nos tratamentos em que define as finalidades e os meios utilizados.
          </Paragraph>
          <ContactCard classes={classes} />
        </Section>

        <Section
          id="papeis"
          title="3. Nossos papéis no tratamento"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Dependendo do contexto, o Fluyra pode atuar como controlador ou como
            operador de dados pessoais:
          </Paragraph>
          <BulletList classes={classes}>
            <li>
              <strong>Controlador:</strong> para dados de cadastro, acesso,
              segurança, contratação, faturamento, suporte e relacionamento
              direto com usuários da plataforma.
            </li>
            <li>
              <strong>Operador:</strong> para contatos, mensagens, anexos e
              demais dados inseridos ou recebidos por uma empresa cliente. Nesse
              caso, a empresa que utiliza o Fluyra define as finalidades do
              tratamento e é a controladora desses dados.
            </li>
          </BulletList>
          <div className={classes.callout}>
            Se você conversou com uma empresa atendida pelo Fluyra, essa empresa
            é o canal principal para pedidos sobre a conversa. Se necessário,
            nós a auxiliaremos no atendimento da solicitação.
          </div>
        </Section>

        <Section id="dados" title="4. Dados que tratamos" classes={classes}>
          <BulletList classes={classes}>
            <li>
              <strong>Cadastro e conta:</strong> nome, e-mail, telefone,
              empresa, perfil de acesso e dados de autenticação protegidos.
            </li>
            <li>
              <strong>Atendimento e CRM:</strong> contatos, identificadores dos
              canais, conteúdo de mensagens, anexos, notas, etiquetas, histórico
              e atividades comerciais.
            </li>
            <li>
              <strong>Integrações:</strong> páginas e contas selecionadas,
              permissões concedidas, identificadores técnicos e credenciais de
              acesso protegidas.
            </li>
            <li>
              <strong>Dados técnicos:</strong> endereço IP, data e hora,
              navegador, dispositivo, registros de acesso, falhas, auditoria e
              eventos de segurança.
            </li>
            <li>
              <strong>Contratação e suporte:</strong> plano, situação da
              assinatura, comunicações de suporte e informações administrativas
              necessárias.
            </li>
          </BulletList>
        </Section>

        <Section
          id="integracoes-meta"
          title="5. Integrações com Facebook e Instagram"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Quando um administrador conecta ativos da Meta ao Fluyra, podemos
            receber e tratar os dados autorizados por ele para viabilizar o
            atendimento em uma caixa de entrada compartilhada. Isso pode
            incluir:
          </Paragraph>
          <BulletList classes={classes}>
            <li>
              identificação e nome de Páginas do Facebook e contas profissionais
              do Instagram;
            </li>
            <li>
              identificadores específicos do canal, nome e foto pública das
              pessoas que iniciam uma conversa;
            </li>
            <li>
              mensagens, respostas, anexos, data e hora, confirmações de entrega
              e leitura e interações com botões;
            </li>
            <li>
              tokens e permissões necessários para receber e responder
              mensagens.
            </li>
          </BulletList>
          <Paragraph classes={classes}>
            As credenciais da integração são protegidas e usadas somente para as
            ações autorizadas. O Fluyra não publica conteúdo em nome do usuário
            nem acessa ativos que não tenham sido selecionados durante a
            conexão.
          </Paragraph>
          <div className={classes.callout}>
            A utilização do Facebook e do Instagram também está sujeita aos
            termos e políticas da Meta Platforms. A integração pode ser
            desconectada a qualquer momento pelo administrador da empresa.
          </div>
        </Section>

        <Section
          id="finalidades"
          title="6. Como utilizamos os dados"
          classes={classes}
        >
          <BulletList classes={classes}>
            <li>
              criar e administrar contas, empresas, usuários e permissões;
            </li>
            <li>
              receber, organizar, encaminhar e permitir respostas a conversas
              nos canais conectados;
            </li>
            <li>
              manter histórico, contexto, indicadores e automações configuradas
              pela empresa cliente;
            </li>
            <li>prestar suporte, diagnosticar falhas e melhorar o serviço;</li>
            <li>
              prevenir fraude, abuso e acesso não autorizado e manter registros
              de auditoria;
            </li>
            <li>cumprir obrigações contratuais, legais e regulatórias.</li>
          </BulletList>
        </Section>

        <Section id="bases-legais" title="7. Bases legais" classes={classes}>
          <Paragraph classes={classes}>
            Conforme a finalidade e a relação com o titular, o tratamento poderá
            se apoiar na execução de contrato ou de procedimentos preliminares,
            no cumprimento de obrigação legal ou regulatória, no exercício
            regular de direitos, no legítimo interesse ou no consentimento,
            quando aplicável.
          </Paragraph>
          <Paragraph classes={classes}>
            Quando atuamos como operador, seguimos as instruções documentadas da
            empresa cliente, que é responsável por definir e informar a base
            legal adequada para o tratamento realizado em seu atendimento.
          </Paragraph>
        </Section>

        <Section
          id="compartilhamento"
          title="8. Compartilhamento de dados"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Dados podem ser compartilhados somente na medida necessária com:
          </Paragraph>
          <BulletList classes={classes}>
            <li>
              usuários autorizados da empresa cliente e seus prestadores de
              atendimento;
            </li>
            <li>
              provedores dos canais conectados, como a Meta Platforms, para
              enviar e receber mensagens;
            </li>
            <li>
              fornecedores de infraestrutura, hospedagem, banco de dados,
              segurança, monitoramento, comunicação e suporte;
            </li>
            <li>
              autoridades públicas ou terceiros, quando houver obrigação legal,
              ordem válida ou necessidade de exercício regular de direitos.
            </li>
          </BulletList>
          <Paragraph classes={classes}>
            Exigimos dos fornecedores medidas de proteção e tratamento
            compatíveis com a natureza dos dados e com a legislação aplicável.
          </Paragraph>
        </Section>

        <Section
          id="transferencias"
          title="9. Transferências internacionais"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Alguns fornecedores de tecnologia ou canais integrados podem
            processar dados fora do Brasil. Nesses casos, adotamos medidas
            contratuais, técnicas e organizacionais adequadas para proteger os
            dados e observar os mecanismos previstos na legislação aplicável.
          </Paragraph>
        </Section>

        <Section
          id="retencao"
          title="10. Retenção e eliminação"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Conservamos os dados pelo período necessário para prestar o serviço,
            cumprir as finalidades informadas, manter a segurança e atender
            prazos legais ou contratuais. Encerrada a necessidade, os dados são
            eliminados ou anonimizados, salvo quando a conservação for permitida
            ou exigida por lei.
          </Paragraph>
          <Paragraph classes={classes}>
            Cópias residuais em backups protegidos são removidas conforme o
            ciclo seguro de retenção e não são utilizadas para finalidades
            correntes. Consulte também nossas{" "}
            <RouterLink to="/exclusao-de-dados" className={classes.inlineLink}>
              instruções de exclusão de dados
            </RouterLink>
            .
          </Paragraph>
        </Section>

        <Section
          id="direitos"
          title="11. Direitos dos titulares"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Nos termos da LGPD, o titular pode solicitar, conforme aplicável:
          </Paragraph>
          <BulletList classes={classes}>
            <li>confirmação da existência de tratamento e acesso aos dados;</li>
            <li>correção de dados incompletos, inexatos ou desatualizados;</li>
            <li>
              anonimização, bloqueio ou eliminação de dados desnecessários,
              excessivos ou tratados em desconformidade;
            </li>
            <li>portabilidade, observada a regulamentação aplicável;</li>
            <li>
              informações sobre compartilhamento e sobre a possibilidade de não
              fornecer consentimento;
            </li>
            <li>
              revogação do consentimento, oposição ao tratamento e eliminação
              dos dados tratados com consentimento, quando cabível;
            </li>
            <li>revisão de decisões automatizadas, quando aplicável.</li>
          </BulletList>
          <Paragraph classes={classes}>
            Para proteger o titular, poderemos solicitar informações razoáveis
            para confirmar sua identidade e o vínculo com os dados. O pedido
            será atendido gratuitamente nos prazos previstos na legislação.
          </Paragraph>
          <ContactCard
            classes={classes}
            subject="Exercício de direitos LGPD — Fluyra"
          />
        </Section>

        <Section
          id="cookies"
          title="12. Cookies e armazenamento local"
          classes={classes}
        >
          <Paragraph classes={classes}>
            O Fluyra utiliza cookies e armazenamento local do navegador
            necessários para autenticação, segurança, preferências de interface
            e continuidade da sessão. Essas tecnologias não devem ser
            desativadas quando forem essenciais ao funcionamento da plataforma.
          </Paragraph>
        </Section>

        <Section id="seguranca" title="13. Segurança" classes={classes}>
          <Paragraph classes={classes}>
            Adotamos controles técnicos e organizacionais proporcionais aos
            riscos, incluindo restrição de acesso, proteção de credenciais,
            registros de auditoria, monitoramento e medidas de segurança na
            comunicação e no armazenamento. Nenhum ambiente é totalmente imune a
            incidentes, por isso os controles são revistos e aprimorados
            continuamente.
          </Paragraph>
        </Section>

        <Section
          id="menores"
          title="14. Crianças e adolescentes"
          classes={classes}
        >
          <Paragraph classes={classes}>
            O Fluyra é uma ferramenta voltada a organizações e profissionais e
            não é direcionado a crianças. Empresas clientes que tratem dados de
            crianças ou adolescentes são responsáveis por observar as exigências
            legais específicas e o melhor interesse desses titulares.
          </Paragraph>
        </Section>

        <Section
          id="alteracoes"
          title="15. Alterações desta política"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Esta política poderá ser atualizada para refletir mudanças legais,
            técnicas ou operacionais. A versão vigente e sua data de atualização
            permanecerão disponíveis nesta página. Alterações relevantes poderão
            ser comunicadas pelos canais usuais da plataforma.
          </Paragraph>
        </Section>

        <Section id="contato" title="16. Contato" classes={classes}>
          <Paragraph classes={classes}>
            Dúvidas, solicitações ou comunicações sobre privacidade e proteção
            de dados podem ser encaminhadas ao canal abaixo.
          </Paragraph>
          <ContactCard classes={classes} />
        </Section>
      </>
    ),
  };
};

const DeletionContent = ({ classes }) => {
  const sections = [
    ["antes-de-solicitar", "1. Antes de solicitar"],
    ["como-solicitar", "2. Como solicitar"],
    ["validacao", "3. Validação da identidade"],
    ["dados-abrangidos", "4. Dados abrangidos"],
    ["meta", "5. Desconectar Facebook e Instagram"],
    ["prazos", "6. Prazos e exceções"],
    ["contato", "7. Canal de atendimento"],
  ];

  return {
    sections,
    intro: (
      <>
        Você pode solicitar a exclusão de dados pessoais tratados pelo Fluyra ou
        revogar uma conexão com o Facebook e o Instagram seguindo estas
        instruções.
      </>
    ),
    content: (
      <>
        <Section
          id="antes-de-solicitar"
          title="1. Antes de solicitar"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Se você é usuário de uma empresa cliente, o administrador da sua
            empresa pode excluir usuários, contatos, conversas e conexões
            conforme as permissões disponíveis no Fluyra.
          </Paragraph>
          <Paragraph classes={classes}>
            Se você apenas conversou com uma empresa pelo Facebook, Instagram ou
            outro canal conectado ao Fluyra, procure primeiro essa empresa. Ela
            é a controladora da conversa e decide sobre a exclusão. Você também
            pode nos escrever, e ajudaremos a encaminhar o pedido quando for
            possível identificar a empresa responsável.
          </Paragraph>
        </Section>

        <Section
          id="como-solicitar"
          title="2. Como solicitar a exclusão"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Envie um e-mail para <strong>{LEGAL_CONTACT_EMAIL}</strong> com o
            assunto <strong>“Exclusão de dados — Fluyra”</strong> e informe:
          </Paragraph>
          <ol className={classes.orderedList}>
            <li>seu nome e o e-mail ou telefone relacionado aos dados;</li>
            <li>o nome da empresa ou organização que utiliza o Fluyra;</li>
            <li>
              se aplicável, a Página do Facebook ou conta do Instagram
              envolvida;
            </li>
            <li>
              uma descrição objetiva dos dados ou da conta que deseja excluir.
            </li>
          </ol>
          <div className={classes.callout}>
            Nunca envie senha, token de acesso, código de autenticação ou
            documento completo no primeiro contato. Solicitaremos somente o
            mínimo necessário para validar o pedido com segurança.
          </div>
          <ContactCard classes={classes} subject="Exclusão de dados — Fluyra" />
        </Section>

        <Section
          id="validacao"
          title="3. Validação da identidade"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Para impedir exclusões indevidas, poderemos confirmar o controle do
            e-mail, da conta ou do ativo conectado e solicitar informações
            adicionais proporcionais ao risco. Quando o pedido envolver dados de
            uma empresa cliente, também poderemos consultar o administrador
            responsável.
          </Paragraph>
        </Section>

        <Section
          id="dados-abrangidos"
          title="4. Dados abrangidos"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Após a validação e conforme o papel do Fluyra no tratamento, a
            exclusão pode abranger:
          </Paragraph>
          <BulletList classes={classes}>
            <li>cadastro e perfil do usuário;</li>
            <li>vínculos com empresas e permissões de acesso;</li>
            <li>contatos, conversas, mensagens e anexos selecionados;</li>
            <li>
              vínculos de Páginas do Facebook e contas profissionais do
              Instagram;
            </li>
            <li>tokens de autorização e demais credenciais da integração;</li>
            <li>dados derivados que não precisem ser conservados.</li>
          </BulletList>
          <Paragraph classes={classes}>
            A exclusão de uma integração impede novas sincronizações, mas não
            elimina automaticamente dados que a empresa cliente precise manter
            por obrigação legal ou para o exercício regular de direitos.
          </Paragraph>
        </Section>

        <Section
          id="meta"
          title="5. Desconectar Facebook e Instagram"
          classes={classes}
        >
          <Paragraph classes={classes}>
            O administrador pode interromper o acesso do Fluyra aos ativos da
            Meta por qualquer uma destas opções:
          </Paragraph>
          <ol className={classes.orderedList}>
            <li>
              no Fluyra, acesse <strong>Conexões</strong>, escolha a conexão do
              Facebook ou Instagram e utilize a opção de desconectar;
            </li>
            <li>
              no Facebook, acesse as configurações da conta e remova o Fluyra em
              <strong> Integrações comerciais</strong>;
            </li>
            <li>
              envie uma solicitação ao nosso canal de privacidade para revogação
              e exclusão dos dados associados.
            </li>
          </ol>
          <Paragraph classes={classes}>
            A remoção da autorização revoga o acesso futuro do Fluyra. Dados
            mantidos pela própria Meta devem ser administrados diretamente nos
            serviços e configurações da Meta.
          </Paragraph>
        </Section>

        <Section id="prazos" title="6. Prazos e exceções" classes={classes}>
          <Paragraph classes={classes}>
            Confirmaremos o recebimento e responderemos nos prazos previstos na
            legislação aplicável. Após a aprovação, a exclusão será executada
            nos sistemas ativos e alcançará backups conforme o ciclo seguro de
            retenção.
          </Paragraph>
          <Paragraph classes={classes}>
            Alguns dados poderão ser conservados quando necessário para cumprir
            obrigação legal ou regulatória, prevenir fraude, resguardar a
            segurança ou exercer direitos em processo judicial, administrativo
            ou arbitral. Nesses casos, o acesso será restrito à finalidade de
            conservação.
          </Paragraph>
        </Section>

        <Section id="contato" title="7. Canal de atendimento" classes={classes}>
          <Paragraph classes={classes}>
            Operador do Fluyra:{" "}
            <strong className={classes.legalName}>{LEGAL_ENTITY_NAME}</strong>.
            Utilize o canal abaixo para acompanhar uma solicitação ou esclarecer
            dúvidas.
          </Paragraph>
          <ContactCard classes={classes} subject="Exclusão de dados — Fluyra" />
        </Section>
      </>
    ),
  };
};

const TermsContent = ({ classes }) => {
  const sections = [
    ["aceite", "1. Aceite e escopo"],
    ["conta", "2. Conta e acesso"],
    ["uso", "3. Uso permitido"],
    ["integracoes", "4. Integrações e canais"],
    ["conteudo", "5. Dados e conteúdo"],
    ["disponibilidade", "6. Disponibilidade"],
    ["propriedade", "7. Propriedade intelectual"],
    ["suspensao", "8. Suspensão e encerramento"],
    ["responsabilidades", "9. Responsabilidades"],
    ["privacidade", "10. Privacidade"],
    ["alteracoes", "11. Alterações"],
    ["legislacao", "12. Legislação aplicável"],
    ["contato", "13. Contato"],
  ];

  return {
    sections,
    intro: (
      <>
        Estes termos estabelecem as condições gerais para acesso e uso do Fluyra
        e complementam a proposta, o plano ou o contrato celebrado com cada
        cliente.
      </>
    ),
    content: (
      <>
        <Section id="aceite" title="1. Aceite e escopo" classes={classes}>
          <Paragraph classes={classes}>
            Ao acessar ou utilizar o Fluyra, o usuário declara ter capacidade e
            autorização para aceitar estes Termos em nome próprio ou da
            organização que representa. Condições comerciais ou contratuais
            específicas prevalecem sobre estes Termos quando houver conflito
            expresso.
          </Paragraph>
          <Paragraph classes={classes}>
            O serviço é fornecido por{" "}
            <strong className={classes.legalName}>{LEGAL_ENTITY_NAME}</strong>,
            sob a marca Fluyra.
          </Paragraph>
        </Section>

        <Section id="conta" title="2. Conta e acesso" classes={classes}>
          <BulletList classes={classes}>
            <li>
              O cliente deve fornecer informações corretas e manter seus dados
              de cadastro atualizados.
            </li>
            <li>
              Credenciais são pessoais e não devem ser compartilhadas. Cada
              usuário é responsável por proteger sua senha e seus métodos de
              autenticação.
            </li>
            <li>
              O administrador da empresa controla usuários, perfis, permissões,
              filas e canais conectados ao seu ambiente.
            </li>
            <li>
              Suspeitas de acesso indevido devem ser comunicadas imediatamente
              ao suporte ou ao canal indicado nestes Termos.
            </li>
          </BulletList>
        </Section>

        <Section id="uso" title="3. Uso permitido" classes={classes}>
          <Paragraph classes={classes}>
            O Fluyra deve ser utilizado de forma lícita, segura e compatível com
            os direitos de terceiros. É proibido:
          </Paragraph>
          <BulletList classes={classes}>
            <li>
              enviar spam, mensagens fraudulentas, abusivas, discriminatórias ou
              ilegais;
            </li>
            <li>
              acessar dados, contas ou ativos sem autorização ou tentar
              contornar controles de segurança;
            </li>
            <li>
              inserir código malicioso, comprometer a disponibilidade do serviço
              ou realizar testes invasivos sem autorização prévia;
            </li>
            <li>
              violar direitos autorais, privacidade, proteção de dados, regras
              de consumo ou políticas dos canais integrados;
            </li>
            <li>
              revender, copiar, desmontar ou explorar a plataforma fora dos
              limites do contrato e da legislação aplicável.
            </li>
          </BulletList>
        </Section>

        <Section
          id="integracoes"
          title="4. Integrações e canais de terceiros"
          classes={classes}
        >
          <Paragraph classes={classes}>
            O cliente somente pode conectar Páginas, contas e demais ativos para
            os quais possua autorização administrativa. Integrações com
            Facebook, Instagram e outros provedores também dependem dos termos,
            políticas, permissões, limites e disponibilidade desses serviços.
          </Paragraph>
          <Paragraph classes={classes}>
            Alterações, bloqueios ou indisponibilidades de terceiros podem
            limitar temporária ou definitivamente uma funcionalidade. O cliente
            é responsável pelo conteúdo enviado e pela observância das regras de
            consentimento, janela de atendimento, publicidade e combate a spam.
          </Paragraph>
        </Section>

        <Section id="conteudo" title="5. Dados e conteúdo" classes={classes}>
          <Paragraph classes={classes}>
            O cliente mantém seus direitos sobre os dados e conteúdos inseridos
            ou recebidos no Fluyra. Ele concede ao Fluyra autorização limitada
            para hospedar, transmitir, organizar, proteger e processar esse
            conteúdo na medida necessária à prestação do serviço.
          </Paragraph>
          <Paragraph classes={classes}>
            O cliente é responsável por possuir base legal e fornecer os avisos
            necessários aos titulares. O Fluyra tratará os dados conforme a
            Política de Privacidade, o contrato e as instruções documentadas do
            cliente.
          </Paragraph>
        </Section>

        <Section
          id="disponibilidade"
          title="6. Disponibilidade e evolução do serviço"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Buscamos manter o serviço seguro e disponível, mas podem ocorrer
            manutenções, atualizações, falhas de infraestrutura ou
            indisponibilidades de terceiros. Funcionalidades podem evoluir para
            melhorar segurança, desempenho, compatibilidade ou conformidade
            legal.
          </Paragraph>
          <Paragraph classes={classes}>
            Níveis de serviço, suporte, limites de uso e condições de
            continuidade são aqueles previstos no plano ou contrato aplicável.
          </Paragraph>
        </Section>

        <Section
          id="propriedade"
          title="7. Propriedade intelectual"
          classes={classes}
        >
          <Paragraph classes={classes}>
            A plataforma, marca, interfaces, documentação, código e demais
            elementos do Fluyra são protegidos pela legislação de propriedade
            intelectual. A contratação concede somente um direito limitado, não
            exclusivo, não transferível e revogável de uso durante a vigência
            contratual.
          </Paragraph>
        </Section>

        <Section
          id="suspensao"
          title="8. Suspensão e encerramento"
          classes={classes}
        >
          <Paragraph classes={classes}>
            O acesso pode ser suspenso em caso de risco à segurança, uso ilegal,
            violação relevante destes Termos, inadimplência ou determinação de
            autoridade competente, observadas as condições contratuais e a
            legislação aplicável. Sempre que viável, o cliente será informado e
            terá oportunidade razoável para corrigir a situação.
          </Paragraph>
        </Section>

        <Section
          id="responsabilidades"
          title="9. Responsabilidades"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Cada parte responde pelos atos sob seu controle. O Fluyra responde
            pela prestação do serviço nos limites da legislação e do contrato
            aplicável; o cliente responde pela legitimidade dos dados, pelas
            configurações de seu ambiente, pelas mensagens enviadas por seus
            usuários e pelo uso dos canais conectados.
          </Paragraph>
          <Paragraph classes={classes}>
            Nenhuma disposição destes Termos exclui direitos ou
            responsabilidades que não possam ser afastados pela legislação
            brasileira, inclusive as normas de proteção de dados e de defesa do
            consumidor quando aplicáveis.
          </Paragraph>
        </Section>

        <Section id="privacidade" title="10. Privacidade" classes={classes}>
          <Paragraph classes={classes}>
            O tratamento de dados pessoais é descrito na{" "}
            <RouterLink
              to="/politica-de-privacidade"
              className={classes.inlineLink}
            >
              Política de Privacidade
            </RouterLink>
            . Pedidos de exclusão podem seguir as{" "}
            <RouterLink to="/exclusao-de-dados" className={classes.inlineLink}>
              instruções de exclusão de dados
            </RouterLink>
            .
          </Paragraph>
        </Section>

        <Section id="alteracoes" title="11. Alterações" classes={classes}>
          <Paragraph classes={classes}>
            Estes Termos poderão ser atualizados para acompanhar mudanças no
            serviço, na legislação ou nos canais integrados. A versão vigente e
            sua data permanecerão disponíveis nesta página. Alterações
            relevantes serão comunicadas por meios adequados.
          </Paragraph>
        </Section>

        <Section
          id="legislacao"
          title="12. Legislação aplicável"
          classes={classes}
        >
          <Paragraph classes={classes}>
            Estes Termos são regidos pela legislação brasileira. Eventuais
            controvérsias observarão o foro definido no contrato aplicável ou,
            na ausência de disposição específica, as regras legais de
            competência, preservados os direitos do consumidor quando
            aplicáveis.
          </Paragraph>
        </Section>

        <Section id="contato" title="13. Contato" classes={classes}>
          <Paragraph classes={classes}>
            Para questões sobre estes Termos ou sobre privacidade, utilize o
            canal abaixo.
          </Paragraph>
          <ContactCard classes={classes} subject="Termos de Uso — Fluyra" />
        </Section>
      </>
    ),
  };
};

const contentByType = {
  privacy: PrivacyContent,
  deletion: DeletionContent,
  terms: TermsContent,
};

const LegalPage = ({ documentType }) => {
  const classes = useStyles();
  const page = legalPages[documentType] || legalPages.privacy;
  const Content = contentByType[documentType] || PrivacyContent;
  const { sections, intro, content } = Content({ classes });
  const PageIcon = page.icon;
  const canonicalUrl = `${PRODUCTION_ORIGIN}${page.path}`;

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{`${page.title} | Fluyra`}</title>
        <meta name="description" content={page.description} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <a href="#conteudo-principal" className={classes.skipLink}>
        Ir para o conteúdo principal
      </a>

      <header className={classes.header}>
        <Container maxWidth="lg" className={classes.headerInner}>
          <RouterLink
            to="/login"
            className={classes.logoLink}
            aria-label="Fluyra"
          >
            <img src={fluyraLogo} alt="Fluyra" className={classes.logo} />
          </RouterLink>
          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            endIcon={<ArrowForward />}
            className={classes.headerAction}
          >
            Acessar plataforma
          </Button>
        </Container>
      </header>

      <main id="conteudo-principal">
        <div className={classes.hero}>
          <Container maxWidth="lg" className={classes.heroContent}>
            <div className={classes.heroBadge}>
              <PageIcon fontSize="small" />
              {page.eyebrow}
            </div>
            <Typography
              component="h1"
              variant="h1"
              className={classes.heroTitle}
            >
              {page.title}
            </Typography>
            <Typography component="p" className={classes.heroSubtitle}>
              {page.description}
            </Typography>
            <div
              className={classes.heroMeta}
              aria-label="Informações do documento"
            >
              <Chip
                variant="outlined"
                size="small"
                label={`Vigente desde ${EFFECTIVE_DATE}`}
                className={classes.metaChip}
              />
              <Chip
                variant="outlined"
                size="small"
                label="Lei nº 13.709/2018 — LGPD"
                className={classes.metaChip}
              />
            </div>
          </Container>
        </div>

        <Container maxWidth="lg" className={classes.pageBody}>
          <nav className={classes.legalNav} aria-label="Documentos legais">
            {Object.entries(legalPages).map(([key, item]) => (
              <Button
                key={key}
                component={RouterLink}
                to={item.path}
                color="primary"
                variant={key === documentType ? "contained" : "outlined"}
                className={classes.navButton}
                aria-current={key === documentType ? "page" : undefined}
              >
                {item.title}
              </Button>
            ))}
          </nav>

          <div className={classes.contentGrid}>
            <nav className={classes.toc} aria-label="Nesta página">
              <Typography className={classes.tocTitle}>Nesta página</Typography>
              <Divider />
              {sections.map(([id, label]) => (
                <a key={id} href={`#${id}`} className={classes.tocLink}>
                  {label}
                </a>
              ))}
            </nav>

            <Paper
              component="article"
              className={classes.article}
              elevation={0}
            >
              <div className={classes.introCard}>
                <div className={classes.introRow}>
                  <div className={classes.introIcon} aria-hidden="true">
                    <LockOutlined />
                  </div>
                  <Typography component="p" className={classes.paragraph}>
                    {intro}
                  </Typography>
                </div>
              </div>
              {content}
            </Paper>
          </div>
        </Container>
      </main>

      <footer className={classes.footer}>
        <Container maxWidth="lg" className={classes.footerInner}>
          <div>
            <Typography component="p">Fluyra CRM</Typography>
            <Typography component="p" variant="body2">
              © 2026. Todos os direitos reservados.
            </Typography>
          </div>
          <nav
            className={classes.footerLinks}
            aria-label="Links legais do rodapé"
          >
            <RouterLink
              to="/politica-de-privacidade"
              className={classes.footerLink}
            >
              Privacidade
            </RouterLink>
            <RouterLink to="/exclusao-de-dados" className={classes.footerLink}>
              Exclusão de dados
            </RouterLink>
            <RouterLink to="/termos-de-uso" className={classes.footerLink}>
              Termos de Uso
            </RouterLink>
          </nav>
        </Container>
      </footer>
    </div>
  );
};

export const PrivacyPolicy = () => <LegalPage documentType="privacy" />;
export const DataDeletion = () => <LegalPage documentType="deletion" />;
export const TermsOfUse = () => <LegalPage documentType="terms" />;

export default LegalPage;
